import { Typography } from '@euler/app/components/typography';
import { ServiceSubjectCategory } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/types';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MaterialTopTabBarProps } from '@react-navigation/material-top-tabs';
import { memo, useCallback } from 'react';
import {
  ScrollView,
  StyleProp,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';

export const ServiceSubjectCategoryTab = memo(
  ({
    categories,
    state,
    navigation,
  }: {
    categories: ServiceSubjectCategory[];
  } & MaterialTopTabBarProps) => {
    const { rpx } = useResponsiveContext();
    const {
      construction: {
        schedule: {
          tab: {
            primary: { activeTextColor, inactiveTextColor },
          },
        },
      },
    } = useTheme();

    return (
      <View
        css={`
          height: ${rpx(50)}px;
          background-color: #fff;
        `}
      >
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingLeft: rpx(5), paddingRight: rpx(5) }}
        >
          {state.routes.map((route, index) => {
            const category = categories[index];
            const focused = state.index === index;
            const textColor = focused ? activeTextColor : inactiveTextColor;
            const onPress = () => {
              const event = navigation.emit({
                type: 'tabPress',
                target: route.key,
                canPreventDefault: true,
              });

              if (!focused && !event.defaultPrevented) {
                // The `merge: true` option makes sure that the params inside the
                // tab screen are preserved
                navigation.navigate({
                  name: route.name,
                  merge: true,
                  params: { category },
                });
              }
            };

            return (
              <TabCell
                key={category.name}
                title={category.name}
                index={index}
                focused={focused}
                titleStyle={{
                  color: textColor,
                }}
                onPress={onPress}
                css={`
                  height: 100%;
                  margin-left: ${rpx(15)}px;
                `}
              />
            );
          })}
        </ScrollView>
      </View>
    );
  },
);

const TabCell = memo(
  ({
    title,
    index,
    style,
    titleStyle,
    focused,
    onPress,
  }: {
    title: string;
    index: number;
    focused?: boolean;
    onPress?: (index: number) => void;
    style?: StyleProp<ViewStyle>;
    titleStyle?: StyleProp<TextStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const onCellPress = useCallback(() => {
      onPress?.(index);
    }, [index, onPress]);
    return (
      <TouchableOpacity
        onPress={onCellPress}
        style={style}
        css={`
          align-items: center;
          justify-content: center;
        `}
      >
        <Typography.Label
          style={titleStyle}
          family={focused ? 'medium' : 'regular'}
        >
          {title}
        </Typography.Label>
        {focused && (
          <Center
            css={`
              position: absolute;
              left: 0;
              right: 0;
              bottom: ${rpx(4)}px;
              height: ${rpx(3)}px;
            `}
          >
            <View
              css={`
                width: 33.3%;
                background-color: #0077ff;
                flex: 1;
                border-radius: ${rpx(1.5)}px;
              `}
            />
          </Center>
        )}
      </TouchableOpacity>
    );
  },
);
